/**
 * Choose a reveal style for a given announcement.
 *
 * Two announcements with the same animationTheme/category MUST be able to
 * land on different styles — variation is the whole point. The seed makes
 * the choice stable (replay = same style) but different per announcement.
 */

import type { RevealStyleId } from './revealStyles';
import type { SeededRng } from './revealSeed';

/**
 * Style weights per animationTheme. Each row sums to ~1.0 conceptually but
 * weights do not need to be normalised — the rng.weighted helper handles it.
 *
 * Order: ethereal_memory, luxury_editorial, emotional_film, intimate_letter, modern_futuristic
 */
const THEME_WEIGHTS: Record<string, [number, number, number, number, number]> = {
  handwritten_journal:  [0.20, 0.10, 0.10, 0.55, 0.05],
  elegant_floral:       [0.35, 0.30, 0.15, 0.15, 0.05],
  golden_light:         [0.30, 0.25, 0.30, 0.10, 0.05],
  blooming_garden:      [0.40, 0.25, 0.15, 0.15, 0.05],
  ornate_keepsake:      [0.20, 0.40, 0.15, 0.20, 0.05],
  memory_constellation: [0.30, 0.15, 0.25, 0.05, 0.25],
};

const DEFAULT_WEIGHTS: [number, number, number, number, number] = [0.25, 0.25, 0.20, 0.20, 0.10];

const STYLES: RevealStyleId[] = [
  'ethereal_memory',
  'luxury_editorial',
  'emotional_film',
  'intimate_letter',
  'modern_futuristic',
];

export interface PickStyleInput {
  rng: SeededRng;
  animationTheme?: string;
  /** Length of the message in characters — used as a tiebreaker. */
  messageLength?: number;
}

export function pickRevealStyle({ rng, animationTheme, messageLength = 0 }: PickStyleInput): RevealStyleId {
  const weights = (animationTheme && THEME_WEIGHTS[animationTheme]) || DEFAULT_WEIGHTS;

  // Long messages bias toward editorial/film for breathing room.
  const adjusted: [number, number, number, number, number] = [...weights] as any;
  if (messageLength > 600) {
    adjusted[1] *= 1.3;
    adjusted[2] *= 1.2;
    adjusted[0] *= 0.9;
  } else if (messageLength < 180) {
    adjusted[3] *= 1.3;
    adjusted[0] *= 1.1;
  }

  return rng.weighted(STYLES, adjusted);
}
